import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { FadeIn } from "@/components/ui/scroll-animations";
import type { ReactNode } from "react";

export function PublicShell({ children }: { children: ReactNode }) {
  return <div className="min-h-screen bg-background text-foreground">{children}</div>;
}

export function Container({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("gc-container", className)}>{children}</div>
  );
}

export function Section({
  children,
  className,
  id,
  variant = "default",
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  variant?: "default" | "muted" | "dark";
}) {
  return (
    <section
      id={id}
      className={cn(
        "gc-section border-b border-border last:border-b-0",
        variant === "muted" && "bg-[hsl(var(--surface))]",
        variant === "dark" && "bg-foreground text-background border-foreground/10",
        className
      )}
    >
      {children}
    </section>
  );
}

export function SectionIntro({
  eyebrow,
  title,
  description,
  align = "left",
  dark = false,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: "left" | "center";
  dark?: boolean;
}) {
  return (
    <FadeIn>
      <div
        className={cn(
          "max-w-3xl mb-14 md:mb-20",
          align === "center" && "mx-auto text-center"
        )}
      >
        {eyebrow && (
          <p
            className={cn(
              "gc-eyebrow mb-4",
              dark ? "text-background/60" : "text-muted-foreground"
            )}
          >
            {eyebrow}
          </p>
        )}
        <h2
          className={cn(
            "gc-heading-section",
            dark ? "text-background" : "text-foreground"
          )}
        >
          {title}
        </h2>
        {description && (
          <p
            className={cn(
              "mt-5 text-base md:text-lg leading-relaxed",
              dark ? "text-background/70" : "text-muted-foreground"
            )}
          >
            {description}
          </p>
        )}
      </div>
    </FadeIn>
  );
}

export function TextLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <Link href={href}>
      <span className="gc-link-arrow group cursor-pointer">
        {children}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

export function StatGrid({
  stats,
  dark = false,
}: {
  stats: readonly { value: string; label: string }[];
  dark?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
      {stats.map((stat, i) => (
        <FadeIn key={stat.label} delay={i * 0.05}>
          <div
            className={cn(
              "p-8 md:p-10",
              dark ? "bg-foreground text-background" : "bg-background"
            )}
          >
            <p className="gc-stat-value">{stat.value}</p>
            <p
              className={cn(
                "gc-stat-label mt-3",
                dark ? "text-background/60" : "text-muted-foreground"
              )}
            >
              {stat.label}
            </p>
          </div>
        </FadeIn>
      ))}
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
}) {
  return (
    <Section className="!pt-16 md:!pt-24">
      <Container>
        <FadeIn>
          {eyebrow && <p className="gc-eyebrow mb-5 text-muted-foreground">{eyebrow}</p>}
          <h1 className="gc-heading-page max-w-4xl">{title}</h1>
          {description && (
            <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              {description}
            </p>
          )}
        </FadeIn>
      </Container>
    </Section>
  );
}
